import React from 'react';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import HeadingItem from '@atlaskit/menu/heading-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import type { RefreshLinkUrlHandler } from './ConnectionsByLineMenu.tsx';
import { ConnectionsByLineMenuItem, type LinkDirection } from './ConnectionsByLineMenuItem.tsx';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';

const styles = cssMap({
	expandButton: {
		paddingInline: token('space.150'),
		paddingBlockStart: token('space.075'),
		paddingBlockEnd: token('space.150'),
	},
});

type LinkSectionHandlers = {
	refreshLinkUrl: RefreshLinkUrlHandler;
	linkDirection: LinkDirection;
	onNavigate?: () => void;
};

type LinkSectionProps = {
	heading: string;
	links: EnhancedSmartLink[];
	compact?: boolean;
} & LinkSectionHandlers;

// A single ungrouped section with a caller-supplied heading listing the given
// links. Renders nothing when there are no links to show.
const ConnectionsByLineLinkSection = ({
	heading,
	links,
	compact,
	refreshLinkUrl,
	linkDirection,
	onNavigate,
}: LinkSectionProps) => {
	if (links.length === 0) {
		return null;
	}

	return (
		<MenuGroup>
			<Section>
				<HeadingItem>{heading}</HeadingItem>
				{links.map((item) => (
					<ConnectionsByLineMenuItem
						key={`${item.url}-${item.occurrenceIndex}`}
						item={item}
						refreshLinkUrl={refreshLinkUrl}
						linkDirection={linkDirection}
						onNavigate={onNavigate}
						compact={compact}
						// This component renders only the popup menu's sections, and every one of
						// them is a plain static list: no Connect, no row actions, no hover card.
						// The preview panel keeps the hover affordances. (CCINT-9250)
						disableHover
					/>
				))}
			</Section>
		</MenuGroup>
	);
};

export type ExpandableLinkSectionProps = {
	heading: string;
	links: EnhancedSmartLink[];
	// The parent decides whether the expand button is visible (e.g. hide it once
	// expanded, or when there is nothing more to show) and supplies the label. This
	// keeps the component generic and reusable for any "show top N, expand to all"
	// link section.
	shouldShowExpandButton?: boolean;
	expandButtonLabel?: string;
	onExpand?: () => void;
	compact?: boolean;
} & LinkSectionHandlers;

export const ConnectionsByLineExpandableLinkSection = ({
	heading,
	links,
	shouldShowExpandButton = false,
	expandButtonLabel,
	onExpand,
	refreshLinkUrl,
	linkDirection,
	onNavigate,
	compact,
}: ExpandableLinkSectionProps): React.JSX.Element => (
	<>
		<ConnectionsByLineLinkSection
			heading={heading}
			links={links}
			compact={compact}
			refreshLinkUrl={refreshLinkUrl}
			linkDirection={linkDirection}
			onNavigate={onNavigate}
		/>
		{shouldShowExpandButton && expandButtonLabel && onExpand ? (
			<Box xcss={styles.expandButton}>
				<Button appearance="default" shouldFitContainer onClick={onExpand}>
					{expandButtonLabel}
				</Button>
			</Box>
		) : null}
	</>
);
